import { useState } from 'react';
import { useViewedVehicle } from '../../hooks/useViewedVehicle';
import { InfoBlock } from '../InfoBlock';
import styled from 'styled-components';
import { AutoCheckReportIcon } from '../../icons/AutoCheckReportIcon';
import { ExpandIcon } from '../../../../../components/Icons/ExpandIcon';
import { CollapseIcon } from '../../../../../components/Icons/CollapseIcon';
import { Icon } from '@material-ui/core';
import { AccidentIcon, OdometerCheckIcon, OwnerIcon, StateTitleBrandIcon, UsageTypeIcon } from './autoCheckReportIcons';
import { AutoCheckPreviewRow } from './AutoCheckPreviewRow';
import { AutoCheckPreviewBanners } from './AutoCheckPreviewBanners';
import { formatCurrency } from 'utils';
import { AutoCheckDamagePopover } from './AutoCheckDamagePopover';
import { AutoCheckItem } from './types';

const Wrapper = styled.div`
  padding: 24px 4px 0 12px;
  display: flex;
  flex-direction: column;
`;

const ViewFullReportButton = styled.a`
  border: 1px solid rgba(34, 90, 145, 1);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
  height: 24px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  align-self: center;
`;

const ReportInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`;

const formatMileageDate = (date: string | null) => {
  if (!date) return 'Unknown date';
  const [year, month, day] = date.split('-');
  return [month, day, year].join('/');
};

const formatMileageValue = (value: number | null) => {
  if (!value) return 'Unknown mileage';
  return formatCurrency(value).slice(1);
};

export const AutoCheckReport = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const [damageExtended, setDamageExtended] = useState(false);

  const toggle = () => setIsExpanded(curr => !curr);

  const vehicle = useViewedVehicle();
  if (!vehicle) return null;

  const openFullReport: React.MouseEventHandler<HTMLAnchorElement> = e => {
    e.preventDefault();
    window.open(`/vehicles/${vehicle.vin}/autocheck-report`, '_blank');
  };

  const autoCheckInfo = vehicle.autocheck_info || {};

  const { value: mileageValue, icon: mileageIcon } = (() => {
    if (autoCheckInfo.has_odometer_issue) {
      return { value: 'Has Issue', icon: 'fail' };
    }

    if (autoCheckInfo.has_mileage_inconsistency) {
      return { value: 'Mileage Inconsistency', icon: 'info' };
    }

    return { value: 'No Issue', icon: 'success' };
  })();

  const reportItems: AutoCheckItem[] = [
    {
      Icon: OwnerIcon,
      label: 'Owner(s)',
      value: autoCheckInfo.owner_count || 0
    },
    {
      Icon: AccidentIcon,
      label: 'Accident / Damage',
      value: autoCheckInfo.has_accident ? 'Has damage/accident' : 'No damage/accident',
      iconType: autoCheckInfo.has_accident ? 'info' : 'success',
      openPopup: () => setDamageExtended(true),
      closePopup: () => setDamageExtended(false),
      PopupComponent:
        autoCheckInfo.has_accident && damageExtended
          ? () => (
              <AutoCheckDamagePopover
                damageInstances={autoCheckInfo.damage_instances || []}
                close={() => setDamageExtended(false)}
              />
            )
          : undefined
    },
    {
      Icon: StateTitleBrandIcon,
      label: 'State Title Brand',
      value: autoCheckInfo.has_state_title_brand ? autoCheckInfo.state_title_brand || 'Yes' : 'Clean',
      iconType: autoCheckInfo.has_state_title_brand ? 'fail' : 'success'
    },

    {
      Icon: UsageTypeIcon,
      label: 'Usage Type',
      value: autoCheckInfo.usage_type || ''
    },
    {
      Icon: OdometerCheckIcon,
      label: 'Odometer Check',
      value: mileageValue,
      iconType: mileageIcon as AutoCheckItem['iconType'],
      subText: autoCheckInfo.last_odometer_value
        ? `Last reported odometer: ${formatMileageValue(autoCheckInfo.last_odometer_value)} (${formatMileageDate(
            autoCheckInfo.last_odometer_check_date
          )})`
        : '',
      subTextPosition: 'bottom'
    }
  ];

  const openItems = reportItems.slice(0, 3);
  const restItems = reportItems.slice(3);

  if (!vehicle.autocheck_info) {
    return (
      <InfoBlock>
        <Wrapper>
          <Row>
            <AutoCheckReportIcon />
            <ViewFullReportButton onClick={openFullReport}>
              <span>View full report</span>
            </ViewFullReportButton>
          </Row>
        </Wrapper>
      </InfoBlock>
    );
  }

  return (
    <InfoBlock>
      <Wrapper>
        <Row>
          <AutoCheckReportIcon />
          <ViewFullReportButton onClick={openFullReport}>
            <span>View full report</span>
          </ViewFullReportButton>
        </Row>
        <ReportInfoWrapper>
          {openItems.map(item => {
            return <AutoCheckPreviewRow key={item.label} {...item} />;
          })}
          {isExpanded &&
            restItems.map(item => {
              return <AutoCheckPreviewRow key={item.label} {...item} />;
            })}
        </ReportInfoWrapper>
        <StyledIcon onClick={toggle}>{isExpanded ? <CollapseIcon /> : <ExpandIcon />}</StyledIcon>
      </Wrapper>
      <AutoCheckPreviewBanners />
    </InfoBlock>
  );
};
